import { Close, InfoOutlined, Search } from '@mui/icons-material';
import {
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  ClickAwayListener,
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const GenSearch = ({
  searchTerm = null,
  placeholder = 'placeholder',
  sx = { width: '210px' },
  defaultValue = '',
  autoFocus = false,
}) => {
  const { t } = useTranslation('global');
  const [search, setSearch] = useState(defaultValue);
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const debouncedDispatch = useMemo(
    () =>
      debounce(() => {
        if (searchTerm) {
          searchTerm(search);
        }
      }, 1000),
    [searchTerm, search]
  );

  const handleInputChange = ({ target }) => {
    setSearch(target.value);
  };

  useEffect(() => {
    setSearch(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    debouncedDispatch();
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch, search]);

  const handleClearText = () => {
    setSearch('');
    searchTerm('');
  };

  return (
    <Box sx={sx}>
      <TextField
        size="small"
        fullWidth
        name="search"
        placeholder={t('search')}
        sx={{
          backgroundColor: theme => theme.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme => theme.palette.divider,
          },
          '& .MuiOutlinedInput-input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          ...(search === ''
            ? {
                endAdornment: (
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip
                      arrow
                      title={placeholder}
                      PopperProps={{
                        disablePortal: true,
                      }}
                      onClose={handleTooltipClose}
                      open={open}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton edge="end" onClick={handleTooltipOpen}>
                        <InfoOutlined />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                ),
              }
            : {
                endAdornment: (
                  <IconButton
                    aria-label="clear-text"
                    onClick={handleClearText}
                    edge="end"
                  >
                    <Close />
                  </IconButton>
                ),
              }),
        }}
        onKeyPress={e => {
          e.key === 'Enter' && e.preventDefault();
        }}
        value={search}
        onChange={handleInputChange}
        autoFocus={autoFocus}
      />
    </Box>
  );
};

export default GenSearch;
